import { PhaserConfig } from '../components/Phaser/types/config';
import { TSound } from '../models/objects/media';
import { getImagePath } from './format/valueFormat';
import { TObstacle, TScene } from '../models/objects/objects';
import { TAnimationConfig, TSprite } from '../models/objects/sprite';

const toNumericId = (value: unknown, fallback: number): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeSceneId = (sceneId: unknown): number => {
    const raw = String(sceneId ?? '').trim();
    const numericPart = raw.startsWith('scene_') ? raw.replace(/^scene_/, '') : raw;
    return toNumericId(numericPart, 1);
};

export interface LessonAssets {
    sprites: TSprite[];
    scenes: TScene[];
    obstacles: TObstacle[];
    sounds: TSound[];
}

// Le catalogue vient de la base avec des chemins relatifs, la config de la lecon
// avec des URLs deja resolues par getImagePath : on compare le nom de fichier
// pour ne pas proposer deux fois le meme asset.
const fileKey = (url: string | undefined): string => {
    const clean = String(url ?? '').split('?')[0];
    const segment = clean.substring(clean.lastIndexOf('/') + 1);
    return segment.toLowerCase();
};

/**
 * Fusionne les assets deja references par la lecon avec le catalogue en base.
 *
 * Les assets de la lecon restent en tete : ils portent les identifiants et les
 * URLs attendus par le moteur Phaser, et ne doivent donc pas etre remplaces par
 * leur equivalent catalogue. Le catalogue ne sert qu'a remplir les listes du
 * configurateur (Scene / Personnages / Objets / Sons).
 */
export const mergeWithCatalog = <T,>(
    lessonAssets: T[],
    catalog: T[],
    getId: (item: T) => number | string | undefined,
    getUrl: (item: T) => string | undefined
): T[] => {
    const ids = new Set<string>();
    const files = new Set<string>();

    lessonAssets.forEach((item) => {
        ids.add(String(getId(item) ?? ''));
        const file = fileKey(getUrl(item));
        if (file) files.add(file);
    });

    const extras = catalog.filter((item) => {
        const file = fileKey(getUrl(item));
        if (file && files.has(file)) return false;
        return !ids.has(String(getId(item) ?? ''));
    });

    return [...lessonAssets, ...extras];
};

export const buildLessonAssetsFromPhaserConfig = (config?: PhaserConfig | null): LessonAssets => {
    if (!config) {
        return {
            sprites: [],
            scenes: [],
            obstacles: [],
            sounds: [],
        };
    }

    const spritesMap = new Map<string, TSprite>();
    (config.characters || []).forEach((character, index) => {
        const key = character.idPhaser || character.name || character.id || `sprite_${index}`;
        if (spritesMap.has(key)) {
            return;
        }

        const animations = (character.animations || []).map((animation, animationIndex) => ({
            ...animation,
            id: toNumericId(animation.id, animationIndex + 1),
        })) as TAnimationConfig[];

        spritesMap.set(key, {
            id: toNumericId(character.id, index + 1),
            name: character.idPhaser || character.name || `sprite_${index + 1}`,
            image_url: getImagePath(character.image_url || ''),
            animations,
        });
    });

    const obstaclesMap = new Map<string, TObstacle>();
    (config.objects || []).forEach((objectConfig, index) => {
        const key = objectConfig.idPhaser || objectConfig.name || objectConfig.id || `obstacle_${index}`;
        if (obstaclesMap.has(key)) {
            return;
        }

        obstaclesMap.set(key, {
            id: toNumericId(objectConfig.id, index + 1),
            name: objectConfig.idPhaser || objectConfig.name || `obstacle_${index + 1}`,
            img: getImagePath(objectConfig.assets?.texture || ''),
        });
    });

    const soundsMap = new Map<string, TSound>();
    (config.sounds || []).forEach((sound, index) => {
        const key = sound.name || sound.id || `sound_${index}`;
        if (soundsMap.has(key)) {
            return;
        }

        soundsMap.set(key, {
            id: toNumericId(sound.id, index + 1),
            file_name: sound.name || `sound_${index + 1}`,
            file_path: sound.file_path || '',
        });
    });

    const scene = config.scene?.id || config.scene?.background?.image
        ? [{
            id: normalizeSceneId(config.scene?.id),
            name: String(config.scene?.id || `scene_${normalizeSceneId(config.scene?.id)}`),
            img: getImagePath(config.scene?.background?.image || ''),
        }]
        : [];

    return {
        sprites: Array.from(spritesMap.values()),
        scenes: scene,
        obstacles: Array.from(obstaclesMap.values()),
        sounds: Array.from(soundsMap.values()),
    };
};